import { useMemo } from 'react'

import { useSelector } from 'react-redux'
import { Input, InputNumber } from 'antd'
import { faCalendar, faClinicMedical, faClock, faComment, faGraduationCap, faCalendarStar, faHospital, faStethoscope, faUsers } from '@fortawesome/pro-solid-svg-icons'
import { faClone, faTrash } from '@fortawesome/pro-duotone-svg-icons'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import FormItem from '../../../../Components/shared/Forms/FormItem'
import IconButton from '../../../../Components/antd/Buttons/IconButton'
import MultiPeriodsPicker from '../../../../Components/antd/Pickers/MultiPeriodsPicker'
import SectionsSelector from '../../../../Components/shared/Selectors/SectionsSelector'
import SectionToYearSelector from '../../../../Components/shared/Selectors/SectionToYearSelector'
import SectorSelector from '../../../../Components/shared/Selectors/SectorSelector'
import HospitalDepartmentsSelector from '../../../../Components/shared/Selectors/HospitalDepartments/HospitalDepartmentsSelector'
import InstitutionsSelector from '../../../../Components/shared/Selectors/Institution/InstitutionsSelector'
import { getEntityOrNull, getIdOrNull, isNonEmptyArray } from '../../../../utils'
import { Properties } from '../../../../utils/entities'
import { MAX_PLACES_PER_REQUEST } from '../../../../utils/entities/internshipRequest'
import { SchoolPortalRequestLine } from '../../../../utils/entities/schoolPortalRequest'

const { COMMENT, DURATION, INSTITUTION, REQUEST_PERIODS, SECTION, SECTION_TO_YEAR, SECTOR, TOTAL } = Properties
const FIELD_STYLE = { width: '100%' }

const PlacesNeedRow = ({
  data, errorFields = [], hospitalDepartments = [], index, institution, institutionGroup, minDate, sections = [], sectionToYears = {},
  onChange, onDuplicate, onRemove
}) => {
  const t = useSelector(selectTranslate)

  const selectedSection = getIdOrNull(data[SECTION])

  const availableSectionToYears = useMemo(
    () => selectedSection === null ? [] : (sectionToYears[selectedSection] ?? []),
    [sectionToYears, selectedSection]
  )

  const isGroupPortal = getIdOrNull(institutionGroup) !== null

  const selectedInstitution = useMemo(() => {
    if (!isGroupPortal) {
      return institution
    }

    return (institutionGroup?.institutions ?? []).find(i => getIdOrNull(i) === getIdOrNull(data[INSTITUTION])) ?? null
  }, [data[INSTITUTION], institution, institutionGroup?.institutions, isGroupPortal])

  const filledDepartments = useMemo(
    () => hospitalDepartments.filter(department => (department?.sectors ?? []).length > 0),
    [hospitalDepartments]
  )

  const availableSectors = useMemo(() => {
    const selectedDepartment = filledDepartments.find(d => getIdOrNull(d) === getIdOrNull(data.hospitalDepartment))

    return (getIdOrNull(selectedDepartment) === null ? selectedInstitution?.sectors : selectedDepartment?.sectors) ?? []
  }, [data.hospitalDepartment, filledDepartments, selectedInstitution?.sectors])

  const update = patch => onChange(new SchoolPortalRequestLine({ ...data, ...patch }))

  const className = field => errorFields.includes(field) ? 'error-ant-border' : ''

  return (
    <div className='bdr-1 bdr-rds-4 pd-12 mb-8'>
      <div className='flex-row ai-ctr jc-between mb-4'>
        <b>{t('school_portal.requests.line', { index: index + 1 })}</b>
        <div className='flex-row ai-ctr'>
          <IconButton
            className='mr-4'
            icon={faClone}
            size='small'
            title={t('school_portal.requests.duplicate_line')}
            onClick={() => onDuplicate(index)}
          />
          <IconButton
            icon={faTrash}
            size='small'
            type='danger'
            title={t('school_portal.requests.remove_line')}
            onClick={() => onRemove(index)}
          />
        </div>
      </div>

      <div className='responsive-flex-row w-100'>
        <FormItem className={'responsive-w-50 pdr-4 ' + className(SECTION)} icon={faGraduationCap} label='form.label.section' required>
          <SectionsSelector
            className={className(SECTION)}
            multiple='default'
            sections={sections}
            style={FIELD_STYLE}
            values={selectedSection}
            onChange={(value, _, entity) => update({ [SECTION]: getEntityOrNull(entity) ?? getEntityOrNull(value), [SECTION_TO_YEAR]: null })}
          />
        </FormItem>
        <FormItem className={'responsive-w-50 pdl-4 ' + className(SECTION_TO_YEAR)} icon={faCalendarStar} label='form.label.year' required>
          <SectionToYearSelector
            className={className(SECTION_TO_YEAR)}
            multiple='default'
            section={selectedSection}
            sectionToYears={availableSectionToYears}
            style={FIELD_STYLE}
            values={selectedSection === null ? null : getIdOrNull(data[SECTION_TO_YEAR])}
            onChange={(value, _, entity) => update({ [SECTION_TO_YEAR]: getEntityOrNull(entity) ?? getEntityOrNull(value) })}
          />
        </FormItem>
      </div>

      <div className='responsive-flex-row w-100'>
        <FormItem className={'responsive-w-50 pdr-4 ' + className(INSTITUTION)} icon={faHospital} label='common.institution.dd' required>
          {isGroupPortal ? (
            <InstitutionsSelector
              className={className(INSTITUTION)}
              institutions={institutionGroup.institutions}
              multiple={false}
              style={FIELD_STYLE}
              values={getIdOrNull(data[INSTITUTION])}
              onChange={(value, _, entity) => update({
                [INSTITUTION]: getEntityOrNull(entity) ?? getEntityOrNull(value),
                hospitalDepartment: null,
                [SECTOR]: null
              })}
            />
          ) : (
            <Input disabled value={institution?.name ?? ''} />
          )}
        </FormItem>
        {!isGroupPortal && isNonEmptyArray(filledDepartments) && (
          <FormItem className='responsive-w-50 pdl-4' icon={faClinicMedical} label='common.hospital_department.dd'>
            <HospitalDepartmentsSelector
              hospitalDepartments={filledDepartments}
              showSearch
              style={FIELD_STYLE}
              values={getIdOrNull(data.hospitalDepartment)}
              onChange={(value, _, entity) => update({ hospitalDepartment: getEntityOrNull(entity) ?? getEntityOrNull(value), [SECTOR]: null })}
            />
          </FormItem>
        )}
      </div>

      <div className='responsive-flex-row w-100'>
        <FormItem className={'w-100 ' + className(SECTOR)} icon={faStethoscope} label='form.label.sector' required>
          <SectorSelector
            className={className(SECTOR)}
            sectors={availableSectors}
            style={FIELD_STYLE}
            values={getIdOrNull(data[SECTOR])}
            onChange={(value, _, entity) => update({ [SECTOR]: getEntityOrNull(entity) ?? getEntityOrNull(value) })}
          />
        </FormItem>
      </div>

      <FormItem icon={faCalendar} label='form.label.periods' required>
        <MultiPeriodsPicker
          className={className(REQUEST_PERIODS)}
          minDate={minDate}
          periods={data[REQUEST_PERIODS]}
          onChange={periods => update({ [REQUEST_PERIODS]: periods })}
        />
      </FormItem>

      <div className='responsive-flex-row w-100'>
        <FormItem className={'responsive-w-50 pdr-4 ' + className(TOTAL)} icon={faUsers} label='school_portal.requests.places' required>
          <InputNumber
            min={1}
            max={MAX_PLACES_PER_REQUEST}
            className={className(TOTAL)}
            style={FIELD_STYLE}
            value={data[TOTAL]}
            onChange={value => update({ [TOTAL]: value })}
          />
        </FormItem>
        <FormItem className='responsive-w-50 pdl-4' icon={faClock} label='form.label.internship_duration'>
          <InputNumber
            min={0}
            style={FIELD_STYLE}
            value={data[DURATION]}
            onChange={value => update({ [DURATION]: value })}
          />
        </FormItem>
      </div>

      <FormItem icon={faComment} label='common.comment.dd'>
        <Input.TextArea
          rows={2}
          value={data[COMMENT] ?? ''}
          onChange={e => update({ [COMMENT]: e.target.value })}
        />
      </FormItem>
    </div>
  )
}

export default PlacesNeedRow
